<template>
  <div class="login-shell">
    <div class="login-splash">
      <div class="login-brand">
        <p class="login-tag">Idle RPG</p>
        <h1>Arcane Instrument Panel</h1>
        <p class="login-subtitle">
          Command your character, orchestrate long actions, and keep the world in view. A calm cockpit for the chaos.
        </p>
      </div>
      <div class="login-highlights">
        <div class="login-highlight">
          <span class="highlight-icon">{{ ICONS.domains.travel }}</span>
          <div>
            <strong>Multitask with clarity.</strong>
            <p>Actions, travel, and combat stay visible while you explore your tools.</p>
          </div>
        </div>
        <div class="login-highlight">
          <span class="highlight-icon">{{ ICONS.actions.key }}</span>
          <div>
            <strong>Sticky action panels.</strong>
            <p>Keep active actions and status cues visible while you move through sessions.</p>
          </div>
        </div>
        <div class="login-highlight">
          <span class="highlight-icon">{{ ICONS.actions.settings }}</span>
          <div>
            <strong>Tactile, responsive UI.</strong>
            <p>Desktop-first controls with mobile-ready layouts and smooth motion.</p>
          </div>
        </div>
      </div>
      <div class="login-footer">
        <span class="login-sigil">✦</span>
        <span>Enter the world. Keep the loop moving.</span>
      </div>
    </div>

    <UiCard class="login-card">
      <div class="login-card__header">
        <p class="login-card__eyebrow">Account Access</p>
        <h2>Sign in to your guild</h2>
        <p class="text-muted">Use your existing credentials or forge a new account.</p>
      </div>

      <form class="login-form" @submit.prevent="handleLogin">
        <label class="login-field">
          <span>Email</span>
          <input v-model="email" class="ui-input" type="email" placeholder="you@example.com" autocomplete="email" />
        </label>
        <label class="login-field">
          <span>Password</span>
          <input v-model="password" class="ui-input" type="password" placeholder="••••••••" autocomplete="current-password" />
        </label>
        <div class="login-actions">
          <UiButton type="submit" :disabled="session.loading">
            <template #icon>{{ ICONS.actions.key }}</template>
            Login
          </UiButton>
          <UiButton type="button" variant="secondary" @click="handleRegister" :disabled="session.loading">
            <template #icon>{{ ICONS.actions.levelUp }}</template>
            Register
          </UiButton>
        </div>
      </form>

      <UiCallout v-if="session.error" variant="danger" class="login-callout">
        {{ session.error }}
      </UiCallout>
      <UiCallout v-else-if="session.isAuthed" variant="success" class="login-callout">
        Signed in as {{ session.player?.email }}. Head to your roster.
      </UiCallout>
    </UiCard>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useSessionStore } from "../../../store/session";
import { useToast } from "../../../composables/useToast";
import { ICONS } from "../../../lib/icons";
import UiButton from "../../../components/ui/UiButton.vue";
import UiCard from "../../../components/ui/UiCard.vue";
import UiCallout from "../../../components/ui/UiCallout.vue";

const session = useSessionStore();
const router = useRouter();
const { addToast } = useToast();

const email = ref("");
const password = ref("");

async function handleLogin() {
  await session.login(email.value, password.value);
  if (session.isAuthed) {
    addToast(`Signed in as ${session.player?.email}`, "success");
    router.push("/characters");
  }
}

async function handleRegister() {
  await session.register(email.value, password.value);
  if (session.isAuthed) {
    addToast(`Welcome, ${session.player?.email}`, "success");
    router.push("/characters");
  }
}
</script>

<style scoped>
.login-shell {
  display: grid;
  grid-template-columns: minmax(280px, 1fr) minmax(320px, 480px);
  gap: 2rem;
  padding: clamp(1.5rem, 4vw, 3rem);
  min-height: 100vh;
}

.login-splash {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 2rem;
  padding: 1rem 2rem 1rem 0;
  min-height: 0;
  max-height: calc(100vh - 4rem);
  overflow: auto;
}

.login-brand h1 {
  font-family: var(--font-display);
  font-size: clamp(2.6rem, 4vw, 3.6rem);
  margin: 0.4rem 0 1rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.login-tag {
  font-size: 0.8rem;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  color: var(--color-muted);
  margin: 0;
}

.login-subtitle {
  color: var(--color-muted);
  font-size: 1.05rem;
  line-height: 1.5;
  max-width: 420px;
}

.login-highlights {
  display: grid;
  gap: 1rem;
}

.login-highlight {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.85rem;
  padding: 1rem 1.2rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--glass-0);
}

.login-highlight p {
  margin: 0.35rem 0 0;
  color: var(--color-muted);
}

.highlight-icon {
  font-size: 1.4rem;
}

.login-footer {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--color-subtle);
  font-size: 0.9rem;
}

.login-sigil {
  font-size: 1.2rem;
  color: var(--color-accent);
}

.login-card {
  align-self: center;
  max-width: 480px;
  width: 100%;
  border: 1px solid var(--color-border-strong);
  background:
    radial-gradient(circle at 20% 0%, color-mix(in srgb, var(--accent-travel) 10%, transparent), transparent 55%),
    radial-gradient(circle at 80% 10%, color-mix(in srgb, var(--accent-social) 10%, transparent), transparent 55%),
    linear-gradient(160deg, color-mix(in srgb, var(--bg-2) 96%, transparent), color-mix(in srgb, var(--bg-1) 88%, transparent));
  position: sticky;
  top: 2rem;
}

.login-card__header {
  display: grid;
  gap: 0.35rem;
  margin-bottom: 1.5rem;
}

.login-card__header h2 {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.04em;
}

.login-card__eyebrow {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.28em;
  font-size: 0.7rem;
  color: var(--color-muted);
}

.login-form {
  display: grid;
  gap: 1rem;
}

.login-field {
  display: grid;
  gap: 0.4rem;
  font-size: 0.9rem;
}

.login-field input {
  width: 100%;
}

.login-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
  margin-top: 0.4rem;
}

.login-callout {
  margin-top: 1rem;
}

@media (max-width: 900px) {
  .login-shell {
    grid-template-columns: 1fr;
  }

  .login-splash {
    padding-right: 0;
    max-height: none;
    overflow: visible;
  }

  .login-card {
    margin: 0 auto;
  }
}
</style>
